import React, { useState } from 'react';
import Button from '../button/Button';
import Spinner from '../Spinner/spinner';
import Modal from '../modal/modal';
import Card from '../card/Card';
import P from '../texts/p/P';
import { updateApplication } from '../../services/ApplicationService';
import { APPLICATION_STATUS } from '../../utils/Constants';
import { useToast } from '../toast/toast';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../routes/routePaths';
import { useClientTheme } from '../../context/ThemeContext';

interface CommitteeButtonProps {
  // Para múltiples carpetas (dashboard)
  selectedApplications?: Set<number>;
  applications?: any[];
  onSuccess?: () => void;

  // Para carpeta individual (contratos)
  cuit?: string;
  applicationId?: string;

  // Configuración del botón
  variant?: 'primary' | 'secondary' | 'tertiary' | 'custom';
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  fullWidth?: boolean;
  disabled?: boolean;
  width?: string;
  height?: string;

  // Configuración del modal
  showDataLossWarning?: boolean;
}

const CommitteeButton: React.FC<CommitteeButtonProps> = ({
  selectedApplications,
  applications = [],
  onSuccess,
  cuit,
  applicationId,
  variant = 'secondary',
  size = 'lg',
  fullWidth = false,
  disabled = false,
  width,
  height,
}) => {
  const { clientTheme } = useClientTheme();
  const navigate = useNavigate();
  const [showCommitteeModal, setShowCommitteeModal] = useState<boolean>(false);
  const [isSendingToCommittee, setIsSendingToCommittee] =
    useState<boolean>(false);

  const isMultipleMode = selectedApplications && applications.length > 0;
  const isSingleMode = cuit && applicationId;

  const handleSendToCommittee = async () => {
    if (isMultipleMode) {
      await handleMultipleApplications();
    } else if (isSingleMode) {
      await handleSingleApplication();
    }
  };

  const handleMultipleApplications = async () => {
    if (!selectedApplications || selectedApplications.size === 0) return;

    setIsSendingToCommittee(true);

    try {
      // Obtener las aplicaciones seleccionadas
      const selectedApps = applications.filter((app) =>
        selectedApplications.has(app.id)
      );

      // Enviar cada aplicación a comité con manejo individual de errores
      const results = await Promise.allSettled(
        selectedApps.map((app) =>
          updateApplication(
            app.cuit,
            {
              status_code: APPLICATION_STATUS.committee.label,
              comment: '',
            },
            app.id.toString()
          )
        )
      );

      // Contar éxitos y errores
      const successful = results.filter(
        (result) => result.status === 'fulfilled'
      ).length;
      const failed = results.filter(
        (result) => result.status === 'rejected'
      ).length;

      if (failed === 0) {
        // Todas exitosas
        useToast.success({
          title: `Se enviaron ${successful} carpetas a comité correctamente.`,
          id: 'committee-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
      } else if (successful > 0) {
        // Algunas exitosas, algunas fallaron
        useToast.error({
          title: `Se enviaron ${successful} carpetas a comité, pero ${failed} no pudieron enviarse.`,
          id: 'committee-toast',
          description:
            'Revisá el listado y intentá nuevamente con las carpetas restantes.',
          closeButton: true,
          autoClose: 8000,
        });
      } else {
        // Todas fallaron
        useToast.error({
          title: 'No se pudo enviar ninguna carpeta a comité.',
          id: 'committee-toast',
          description: 'Verificá la conexión e intentá nuevamente.',
          closeButton: true,
          autoClose: 8000,
        });
      }

      setShowCommitteeModal(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      useToast.error({
        title: 'Ocurrió un error inesperado al enviar las carpetas a comité.',
        id: 'committee-toast',
        description: 'Intentá nuevamente.',
        closeButton: true,
        autoClose: 5000,
      });
    } finally {
      setIsSendingToCommittee(false);
    }
  };

  const handleSingleApplication = async () => {
    setIsSendingToCommittee(true);

    try {
      const response = await updateApplication(
        cuit!,
        {
          status_code: APPLICATION_STATUS.committee.label,
          comment: '',
        },
        applicationId!
      );

      if (response.status === 200) {
        useToast.success({
          title: 'Se envió la carpeta a comité correctamente.',
          id: 'committee-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });

        setShowCommitteeModal(false);
        setTimeout(() => {
          navigate(routes.dashboard);
        }, 1000);
      } else {
        throw new Error('Error en la respuesta del servidor');
      }
    } catch (error) {
      useToast.error({
        title: 'No se pudo enviar la carpeta a comité.',
        id: 'committee-toast',
        description: 'Verificá la conexión e intentá nuevamente.',
        closeButton: true,
        autoClose: 8000,
      });
    } finally {
      setIsSendingToCommittee(false);
    }
  };

  const renderCommitteeModal = () => {
    const isMultiple = isMultipleMode && selectedApplications;
    const count = isMultiple ? selectedApplications.size : 1;
    const text = isMultiple ? 'carpetas' : 'carpeta';

    return (
      <Card>
        <P type='1' variant='neutral' withMargin={false}>
          Si confirmás, {count} {text} {isMultiple ? 'volverán' : 'volverá'} al
          estado de "En comité" para ser {isMultiple ? 'revisadas' : 'revisada'}{' '}
          nuevamente. Podrás {isMultiple ? 'encontrarlas' : 'encontrarla'} en el
          listado Comité.
        </P>
        <br />

        <P type='2' variant='neutral' weight='bold' withMargin={false}>
          ¿Estás seguro de continuar?
        </P>
      </Card>
    );
  };

  const isDisabled =
    disabled ||
    isSendingToCommittee ||
    (isMultipleMode && selectedApplications?.size === 0);

  if (variant === 'custom') {
    const primaryColor = clientTheme?.primaryColor || '#03BAA1';
    const primaryColorWeak = clientTheme?.primaryColorWeak || '#DDF0ED';
    
    return (
      <>
        <button
          onClick={() => setShowCommitteeModal(true)}
          disabled={isDisabled}
          style={{
            width: width || '200px',
            height: height || '40px',
            minWidth: width ? 'auto' : '180px',
            maxWidth: width || '200px',
            fontWeight: '600',
            letterSpacing: '0.8px',
            padding: '8px 16px',
            backgroundColor: isDisabled ? primaryColorWeak : primaryColor,
            color: isDisabled ? '#9CA3AF' : '#FFFFFF',
            border: 'none',
            borderRadius: '8px',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontFamily: 'Inter',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isSendingToCommittee ? (
            <Spinner size='sm' variant='primary' />
          ) : (
            'Devolver a Comité'
          )}
        </button>

        <Modal
          isOpen={showCommitteeModal}
          onClose={() => setShowCommitteeModal(false)}
          title={'Devolver a Comité'}
          primaryText='Sí, devolver a comité'
          primaryVariant='danger'
          secondaryText='Cancelar'
          secondaryVariant='secondary'
          onSecondaryAction={() => {
            setShowCommitteeModal(false);
          }}
          onPrimaryAction={handleSendToCommittee}
          loadingPrimaryBtn={isSendingToCommittee}
          children={renderCommitteeModal()}
        />
      </>
    );
  }

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setShowCommitteeModal(true)}
        fullWidth={fullWidth}
        disabled={isDisabled}
      >
        {isSendingToCommittee ? (
          <Spinner size='sm' variant='primary' />
        ) : (
          'Devolver a Comité'
        )}
      </Button>

      <Modal
        isOpen={showCommitteeModal}
        onClose={() => setShowCommitteeModal(false)}
        title={'Devolver a Comité'}
        primaryText='Sí, devolver a comité'
        primaryVariant='danger'
        secondaryText='Cancelar'
        secondaryVariant='secondary'
        onSecondaryAction={() => {
          setShowCommitteeModal(false);
        }}
        onPrimaryAction={handleSendToCommittee}
        loadingPrimaryBtn={isSendingToCommittee}
        children={renderCommitteeModal()}
      />
    </>
  );
};

export default CommitteeButton;
